import axios, {
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import ApiError from "./api-error";

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Define a generic type for API response data
type ApiResponse<T> = Promise<T>;

// Delete function with types for URL and data
function deleteApi<T>(url: string, data: any): ApiResponse<T> {
  return axiosClient
    .delete(url, { data: data })
    .then((response: AxiosResponse<T>) => response.data)
    .catch((error: any) => {
      throw new ApiError(error);
    });
}

// Post function with types for URL and data
function post<T>(url: string, data: any): ApiResponse<T> {
  return axiosClient
    .post(url, data)
    .then((response: AxiosResponse<T>) => response.data)
    .catch((error: any) => {
      throw new ApiError(error);
    });
}

// Put function with types for URL and data
function put<T>(url: string, data: any): ApiResponse<T> {
  return axiosClient
    .put(url, data)
    .then((response: AxiosResponse<T>) => response.data)
    .catch((error: any) => {
      throw new ApiError(error);
    });
}

function patch<T>(url: string, data: any): ApiResponse<T> {
  return axiosClient
    .patch(url, data)
    .then((response: AxiosResponse<T>) => response.data)
    .catch((error: any) => {
      throw new ApiError(error);
    });
}

// Get function with type for URL
function get<T>(url: string): ApiResponse<T> {
  return axiosClient
    .get(url)
    .then((response: AxiosResponse<T>) => response.data)
    .catch((error: any) => {
      throw new ApiError(error);
    });
}

// Automatically add token from localStorage to each request
axiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("accessToken"); // Get token from localStorage

    if (token && config.headers) {
      config.headers["Token"] = `${token}`; // Attach the token to the request
    }

    return config;
  },
  (error) => Promise.reject(error)
);

async function apiFilePost<T>(
  url: string,
  data: T | FormData,
  isMultipart: boolean = false,
  responseType: "json" | "blob" = "json"
): Promise<T> {
  try {
    const headers = isMultipart
      ? { "Content-Type": "multipart/form-data" }
      : undefined;

    const response: AxiosResponse<T> = await axiosClient.post(url, data, {
      headers,
      responseType,
    });
    return response.data;
  } catch (error) {
    throw new ApiError(error as AxiosError);
  }
}

// Function to clear API access token by clearing the interceptor
function clearApiAccessToken(): void {
  axiosClient.interceptors.request.clear();
}

const apiClient = { post, get, put, patch, deleteApi, apiFilePost };

export { apiClient, clearApiAccessToken };
